<template>
  <div class="blueprint-editor-root">
    <div v-for="(field, index) in fields" :key="index" class="field-card" :class="getClass(field)">
      <template v-if="field.isEditing">
        <div class="edit-fields">
          <!-- For Bedrock: allow editing key; For Textract: disable key editing -->
          <div v-if="!props.isTextractReadOnly" class="edit-label">{{ $t('editBlueprint.blueprintEditor.label') }}</div>
          <input v-if="!props.isTextractReadOnly" class="edit-input" v-model="fields[index].displayKey" :placeholder="$t('editBlueprint.blueprintEditor.labelPlaceholder')" autocomplete="off" @input="forceFieldUpdate(index)" />
          <div v-if="props.isTextractReadOnly">
            <div class="edit-label">{{ $t('editBlueprint.blueprintEditor.label') }}</div>
            <input class="edit-input edit-input-readonly" :value="fields[index].displayKey || fields[index].key" readonly />
          </div>
          <div class="edit-label instruction-label-row">
            <span>{{ $t('editBlueprint.blueprintEditor.instruction') }}</span>
            <button class="alias-add-btn" @click="openAliasPopup(index)" :title="$t('editBlueprint.blueprintEditor.addMoreAlias')">
              <svg class="icon" viewBox="0 0 24 24">
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
              </svg>
            </button>
          </div>
          <textarea class="edit-input" v-model="fields[index].instruction" rows="2" :placeholder="$t('editBlueprint.blueprintEditor.instructionPlaceholder')"></textarea>
        </div>
        <div class="field-actions editing-actions">
          <button class="icon-btn" @click="handleSave(index)" :title="$t('editBlueprint.blueprintEditor.save')">
            <!-- New Save Icon: Material Design "Check" -->
            <svg class="icon" viewBox="0 0 24 24">
              <path fill="currentColor" d="M9 16.2l-3.5-3.5a1 1 0 1 1 1.4-1.4l2.1 2.09 5.59-5.59a1 1 0 1 1 1.41 1.41l-6.3 6.29a1 1 0 0 1-1.4 0z" />
            </svg>
          </button>
          <button class="icon-btn" @click="handleEditToggle(index)" :title="$t('editBlueprint.blueprintEditor.cancel')">
            <svg class="icon" viewBox="0 0 24 24">
              <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>
      </template>
      <template v-else>
        <div class="field-row">
          <div class="field-title-group" @click="emit('focusField', field.key)">
            <div class="field-title" :title="field.key.replaceAll('_', ' ')">
              {{ field.key.replaceAll('_', ' ') }}
            </div>
            <div class="field-value-confidence-row">
              <span
                class="field-value"
                :class="{
                  'field-value-italic': field.value === 'Update document to extract value',
                }"
                :title="field.value"
              >
                {{ field.value }}
              </span>
              <span class="field-confidence" v-if="field.confidence !== undefined && field.value !== '' && field.value !== null" :title="'Confidence: ' + Math.round(field.confidence * 100) + '%'"> {{ Math.round(props.isTextractReadOnly ? field.confidence : field.confidence * 100) }}% </span>
            </div>
          </div>
          <div class="field-actions">
            <!-- For Textract: show edit button only (for instruction editing) -->
            <template v-if="props.isTextractReadOnly">
              <button class="icon-btn" @click="handleEditToggle(index)" :title="$t('editBlueprint.blueprintEditor.edit')" v-if="isEditMode">
                <svg class="icon" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zm17.71-10.04c.39-.39.39-1.02 0-1.41l-2.54-2.54c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                </svg>
              </button>
            </template>
            <!-- For Bedrock: show both edit and delete buttons -->
            <template v-else>
              <button class="icon-btn" @click="handleEditToggle(index)" :title="$t('editBlueprint.blueprintEditor.edit')" v-if="isEditMode">
                <svg class="icon" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zm17.71-10.04c.39-.39.39-1.02 0-1.41l-2.54-2.54c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                </svg>
              </button>
              <button class="icon-btn" @click="handleDelete(index)" :title="$t('editBlueprint.blueprintEditor.delete')" v-if="isEditMode">
                <svg class="icon" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12zm3.46-7.12L12 13.41l2.54-2.53 1.42 1.42L13.41 14.83l2.53 2.54-1.42 1.42L12 16.25l-2.54 2.54-1.42-1.42 2.53-2.54-2.53-2.54 1.42-1.42zM15.5 4l-1-1h-5l-1 1H5v2h14V4h-3.5z" />
                </svg>
              </button>
            </template>
          </div>
        </div>
      </template>
    </div>
    <div v-if="confirmOpen" class="dialog-backdrop">
      <div class="dialog-card">
        <div class="dialog-title">{{ $t('editBlueprint.blueprintEditor.confirmDeletion') }}</div>
        <div class="dialog-body">{{ $t('editBlueprint.blueprintEditor.confirmDeletionMessage') }}</div>
        <div class="dialog-actions">
          <button class="dialog-btn outlined" @click="confirmOpen = false">{{ $t('editBlueprint.blueprintEditor.cancel') }}</button>
          <button class="dialog-btn error" @click="confirmDelete">{{ $t('editBlueprint.blueprintEditor.delete') }}</button>
        </div>
      </div>
    </div>

    <!-- Alias Management Popup -->
    <div v-if="aliasPopupOpen" class="dialog-backdrop">
      <div class="alias-dialog-card">
        <!-- Header -->
        <div class="alias-dialog-header">
          <div class="dialog-title">{{ $t('editBlueprint.blueprintEditor.manageAliases') }} - {{ currentFieldIndex !== null ? fields[currentFieldIndex].displayKey || fields[currentFieldIndex].key : '' }}</div>
        </div>

        <!-- Scrollable Body -->
        <div class="alias-dialog-body">
          <!-- Current Aliases List -->
          <div class="aliases-section">
            <div class="section-label">{{ $t('editBlueprint.blueprintEditor.currentAliases') }}: {{ currentAliases.length }}</div>
            <div v-if="currentAliases.length > 0" class="aliases-list">
              <span v-for="(alias, index) in currentAliases" :key="index" class="alias-tag">
                {{ alias }}
                <button class="remove-alias-btn" @click="removeAlias(index)" :title="$t('editBlueprint.blueprintEditor.removeAlias')">
                  <svg class="icon" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </span>
            </div>
            <div v-else class="no-aliases-message">{{ $t('editBlueprint.blueprintEditor.noAliasesYet') }}</div>
          </div>

          <!-- Add New Alias -->
          <div class="add-alias-section">
            <div class="section-label">{{ $t('editBlueprint.blueprintEditor.addNewAlias') }}</div>
            <div class="alias-input-row">
              <input v-model="newAliasInput" @keyup.enter="addAlias" class="alias-input" :placeholder="$t('editBlueprint.blueprintEditor.enterAlias')" autocomplete="off" />
              <button @click="addAlias" class="add-btn" :disabled="!newAliasInput.trim()">{{ $t('editBlueprint.blueprintEditor.add') }}</button>
            </div>
            <!-- Error Message -->
            <div v-if="aliasError" class="alias-error-message">
              <svg class="icon error-icon" viewBox="0 0 24 24">
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              {{ aliasError }}
            </div>
          </div>

          <!-- OR Divider -->
          <div class="or-divider">
            <div class="or-line"></div>
            <span class="or-text">{{ $t('editBlueprint.blueprintEditor.or') }}</span>
            <div class="or-line"></div>
          </div>

          <!-- AI Generation -->
          <div class="ai-generation-section">
            <button @click="generateWithAI" class="ai-generate-btn" :disabled="isGeneratingAI">
              <svg class="icon" viewBox="0 0 24 24" v-if="!isGeneratingAI">
                <path fill="currentColor" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <div v-if="isGeneratingAI" class="loading-spinner"></div>
              {{ isGeneratingAI ? $t('editBlueprint.blueprintEditor.generating') : $t('editBlueprint.blueprintEditor.generateWithAI') }}
            </button>
          </div>

          <!-- Final Instruction Preview -->
          <div class="instruction-preview-section">
            <div class="section-label">{{ $t('editBlueprint.blueprintEditor.finalInstructionPreview') }}</div>
            <div class="character-count">{{ finalInstructionText.length }}{{ $t('editBlueprint.blueprintEditor.charactersLimit') }}</div>
            <textarea v-model="finalInstructionText" class="instruction-preview" rows="3" placeholder="Final instruction will appear here" :class="{ 'over-limit': finalInstructionText.length > 300 }"></textarea>
          </div>
        </div>

        <!-- Footer -->
        <div class="alias-dialog-footer">
          <button class="dialog-btn outlined" @click="closeAliasPopup">{{ $t('editBlueprint.blueprintEditor.cancel') }}</button>
          <button class="dialog-btn" @click="saveAliases" :disabled="finalInstructionText.length > 300">{{ $t('editBlueprint.blueprintEditor.updateInstruction') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, getCurrentInstance } from 'vue'

// Get current instance to access $t function
const instance = getCurrentInstance()
const $t = instance.proxy.$t

const props = defineProps({
  blueprintSchema: { type: Object, required: true },
  onUpdate: Function,
  focusedFieldKey: String,
  isEditMode: Boolean,
  isTextractReadOnly: { type: Boolean, default: false },
})
const emit = defineEmits(['focusField'])
const deleteIndex = ref(null)
const confirmOpen = ref(false)
const fields = ref([])

// Alias management data
const aliasPopupOpen = ref(false)
const currentFieldIndex = ref(null)
const currentAliases = ref([])
const newAliasInput = ref('')
const baseInstruction = ref('')
const isGeneratingAI = ref(false)
const aliasError = ref('')

// Computed property for final instruction text
const finalInstructionText = computed({
  get() {
    return formatFinalInstruction(baseInstruction.value, currentAliases.value)
  },
  set(value) {
    // Allow manual editing of the final instruction
    const parsed = parseInstruction(value)
    baseInstruction.value = parsed.baseInstruction
    currentAliases.value = parsed.aliases
  },
})

// Utility function to format final instruction
function formatFinalInstruction(instruction, aliases) {
  if (!instruction) return ''

  let result = instruction.trim()
  // Remove existing "Also mentioned as" part if it exists
  result = result.replace(/\.\s*Also mentioned as[^.]*$/i, '')

  if (aliases && aliases.length > 0) {
    const aliasText = aliases.map((alias) => `"${alias}"`).join(', ')
    result += `. Also mentioned as ${aliasText}`
  }

  return result
}

// Utility function to parse existing instruction and extract base + aliases
function parseInstruction(instruction) {
  if (!instruction) return { baseInstruction: '', aliases: [] }

  const alsoMentionedMatch = instruction.match(/^(.+?)\.\s*Also mentioned as\s+(.+)$/i)

  if (alsoMentionedMatch) {
    const baseInstruction = alsoMentionedMatch[1].trim()
    const aliasSection = alsoMentionedMatch[2].trim()

    // Extract aliases from quoted strings
    const aliases = []
    const aliasMatches = aliasSection.match(/"([^"]+)"/g)
    if (aliasMatches) {
      aliasMatches.forEach((match) => {
        const alias = match.replace(/"/g, '').trim()
        if (alias) aliases.push(alias)
      })
    }

    return { baseInstruction, aliases }
  }

  return { baseInstruction: instruction, aliases: [] }
}

// Initialize fields from blueprintSchema
function resetFieldsFromSchema(schema) {
  if (schema?.properties) {
    fields.value = Object.entries(schema.properties).map(([key, value]) => ({
      key,
      ...value,
      isEditing: false,
      displayKey: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), // Capitalize each word
    }))
  }
}
resetFieldsFromSchema(props.blueprintSchema)
// Watch for blueprintSchema changes
watch(
  () => props.blueprintSchema,
  (newVal) => {
    if (newVal?.properties) {
      resetFieldsFromSchema(newVal)
    }
  },
  { deep: true }
)

watch(
  () => props.focusedFieldKey,
  () => {
    // Force re-evaluation by triggering reactivity
    fields.value = [...fields.value]
  }
)
const getClass = (field) => {
  return {
    editing: field.isEditing,
    selected: field.key === props.focusedFieldKey,
  }
}
// // Watch for changes in the fields array
// watch(
//   fields,
//   (newFields) => {
//     newFields.forEach((field) => {
//       field.key = field.displayKey
//         .toLowerCase()
//         .trim()
//         .replace(/\s+/g, "_") // space to underscore
//         .replace(/[^\w_]/g, ""); // optional: remove special characters
//     });
//   },
//   { deep: true }
// );
function handleEditToggle(index) {
  fields.value[index].isEditing = !fields.value[index].isEditing
}

function forceFieldUpdate(index) {
  // Force reactivity for the specific field
  const field = fields.value[index]
  fields.value[index] = { ...field }
}

function handleDelete(index) {
  deleteIndex.value = index
  confirmOpen.value = true
}

function handleSave(index) {
  // For Bedrock: Update the key if displayKey has changed
  // For Textract: Skip key updates (read-only)
  if (!props.isTextractReadOnly && fields.value[index].displayKey !== fields.value[index].key) {
    const newKey = fields.value[index].displayKey
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^\w_]/g, '')

    // Update the key
    fields.value[index].key = newKey
  }

  fields.value[index].isEditing = false
  // Force reactivity by reassigning the array
  fields.value = [...fields.value]
  emitUpdate()
}

function emitUpdate() {
  if (props.onUpdate) {
    const updatedProperties = {}
    fields.value.forEach((field) => {
      updatedProperties[field.key] = {
        ...field,
        displayKey: undefined,
        isEditing: undefined,
      }
    })
    // Force reactivity update
    const updatedSchema = { ...props.blueprintSchema, properties: updatedProperties }
    props.onUpdate(updatedSchema)
  }
}

function confirmDelete() {
  const updated = [...fields.value]
  updated.splice(deleteIndex.value, 1)
  fields.value = updated
  confirmOpen.value = false
  emitUpdate()
}

// Alias popup management methods
function openAliasPopup(index) {
  currentFieldIndex.value = index
  const currentInstruction = fields.value[index].instruction || ''

  // Parse existing instruction
  const parsed = parseInstruction(currentInstruction)
  baseInstruction.value = parsed.baseInstruction
  currentAliases.value = [...parsed.aliases]

  newAliasInput.value = ''
  aliasPopupOpen.value = true
}

function closeAliasPopup() {
  aliasPopupOpen.value = false
  currentFieldIndex.value = null
  currentAliases.value = []
  baseInstruction.value = ''
  newAliasInput.value = ''
  isGeneratingAI.value = false
  aliasError.value = ''
}

function addAlias() {
  const alias = newAliasInput.value.trim()
  if (!alias) return

  // Clear previous error
  aliasError.value = ''

  // Check if adding this alias would exceed character limit
  const testAliases = [...currentAliases.value, alias]
  const testInstruction = formatFinalInstruction(baseInstruction.value, testAliases)

  if (testInstruction.length <= 300) {
    currentAliases.value.push(alias)
    newAliasInput.value = ''
  } else {
    const charactersOver = testInstruction.length - 300
    aliasError.value = $t('editBlueprint.blueprintEditor.aliasLengthError', { count: charactersOver })
  }
}

function removeAlias(index) {
  currentAliases.value.splice(index, 1)
  // Clear error when removing alias as it might fix the character limit issue
  aliasError.value = ''
}

function saveAliases() {
  if (currentFieldIndex.value !== null && finalInstructionText.value.length <= 300) {
    fields.value[currentFieldIndex.value].instruction = finalInstructionText.value
    closeAliasPopup()
  }
}

// AI Generation method (dummy implementation)
async function generateWithAI() {
  if (currentFieldIndex.value === null) return

  isGeneratingAI.value = true
  // Clear previous errors
  aliasError.value = ''

  try {
    const fieldLabel = fields.value[currentFieldIndex.value].displayKey || fields.value[currentFieldIndex.value].key
    const currentInstruction = baseInstruction.value || fields.value[currentFieldIndex.value].instruction

    // API call to generate aliases
    const response = await generateAliasesFromAPI(fieldLabel, currentInstruction)

    if (response && response.instruction) {
      const parsed = parseInstruction(response.instruction)
      baseInstruction.value = parsed.baseInstruction
      currentAliases.value = parsed.aliases
    }
  } catch (error) {
    console.error('Error generating aliases:', error)
    aliasError.value = $t('editBlueprint.blueprintEditor.failedToGenerateAliases')
  } finally {
    isGeneratingAI.value = false
  }
}

// API function to generate aliases using backend
async function generateAliasesFromAPI(label, instruction) {
  try {
    const { post } = await import('../../../utils/api.js')

    const response = await post('/v1/bedrock/generate-aliases', {
      label: label,
      instruction: instruction,
      document_class: props?.blueprintSchema?.class.replaceAll('_', ' ') || '',
    })

    if (response.data.success) {
      return {
        instruction: response.data.instruction,
      }
    } else {
      throw new Error(response.data.error || 'Failed to generate aliases')
    }
  } catch (error) {
    console.error('API Error generating aliases:', error)
    throw new Error(`Failed to generate aliases: ${error.message}`)
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&display=swap');

.field-card.selected {
  border: 2px solid #1976d2;
  background-color: #e3f2fd;
}

.blueprint-editor-root {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0px 0px 10px 0px;
}

.field-card {
  width: 100%;
  background: var(--secondary-color);
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  margin: 0;
  padding: 0;
  position: relative;
  transition: box-shadow 0.15s, border 0.15s;
  box-shadow: 0 1px 4px 0 #e7eaf333;
  min-height: 36px;
  overflow: hidden;
}

.field-card:not(.editing):hover {
  border: 1.5px solid #1976d2;
  box-shadow: 0 2.5px 10px 0 #c7dbfa7a;
}

.field-row {
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-height: 36px;
  gap: 10px;
  padding: 0 8px;
  position: relative;
}

.field-title-group {
  display: flex;
  flex-direction: column;
  min-width: 110px;
  flex: 1 1 300px;
  max-width: 85vw;
}

.field-title {
  font-size: 13.2px;
  font-weight: 600;
  font-family: 'Archivo', Arial, sans-serif;
  color: var(--primary-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 6px;
  margin-bottom: 2px;
  text-transform: capitalize;
}

.field-value-confidence-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  margin-bottom: 2px;
}

.field-value {
  font-size: 12.8px;
  font-family: 'Archivo', Arial, sans-serif;
  color: #445;
  font-weight: 500;
  white-space: pre-wrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 80%;
  line-height: 1.35;
  word-break: break-word;
  background: transparent;
}

.field-value-italic {
  font-size: 12.8px;
  font-style: italic;
  font-family: 'Archivo', Arial, sans-serif;
  color: #445;
  font-weight: 500;
  white-space: pre-wrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 60vw;
  line-height: 1.35;
  word-break: break-word;
  background: transparent;
}

.field-confidence {
  display: flex;
  align-items: center;
  gap: 2px;
  background: #eafbe8;
  border-radius: 4px;
  padding: 0 4px 0 4px;
  font-size: 11px;
  color: #388e3c;
  font-family: 'Archivo', Arial, sans-serif;
  font-weight: 600;
  height: 18px;
  white-space: nowrap;
}

.confidence-icon {
  width: 13px;
  height: 13px;
  margin-right: 1px;
}

.field-actions {
  display: flex;
  gap: 1px;
  min-width: 55px;
  justify-content: flex-end;
  align-items: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.13s;
  position: absolute;
  right: 8px;
  top: 8px;
  padding-right: 15px;
}

.field-card:hover .field-actions,
.field-card.editing .field-actions {
  opacity: 1;
  pointer-events: auto;
}

.editing-actions {
  position: absolute;
  right: 8px;
  top: 10px;
  opacity: 1 !important;
  pointer-events: auto !important;
  background: transparent;
  z-index: 2;
}

.icon-btn {
  background: none;
  border: none;
  font-size: 1.03rem;
  font-family: 'Archivo', Arial, sans-serif;
  padding: 3px;
  border-radius: 5px;
  cursor: pointer;
  color: #546e7a;
  transition: background 0.15s, color 0.18s;
  display: flex;
  align-items: center;
}

.icon-btn:hover {
  color: #164863;
  background: #e3f0fa;
}

.icon {
  width: 18px;
  height: 18px;
  display: block;
}

.field-card.editing {
  background: var(--secondary-color);
  min-height: 140px;
  box-shadow: 0 2px 12px 0 #b6d7f333;
  border: 1.5px solid var(--primary-color);
  padding: 8px 26px 12px 16px;
  /* width: 90%; */
  position: relative;
}

.edit-fields {
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 100%;
}

.edit-label {
  font-size: 12.2px;
  font-weight: 600;
  font-family: 'Archivo', Arial, sans-serif;
  margin-top: 4px;
  color: #164863;
}

.edit-input {
  font-size: 12.5px;
  font-family: 'Archivo', Arial, sans-serif;
  padding: 7px 8px;
  border: 1px solid #c2c8cc;
  border-radius: 6px;
  background: #f9fafd;
  outline: none;
  width: 100%;
  resize: vertical;
  transition: border 0.12s;
}

.edit-input:focus {
  border-color: #164863;
}

.edit-input-readonly {
  background-color: #f0f0f0 !important;
  cursor: not-allowed !important;
  color: #555 !important;
}

.edit-input-readonly:focus {
  border-color: #c2c8cc !important;
  outline: none !important;
}

/* Instruction label with alias button */
.instruction-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.alias-add-btn {
  background: none;
  border: none;
  padding: 2px;
  border-radius: 3px;
  cursor: pointer;
  color: #1976d2;
  transition: background 0.15s, color 0.18s;
  display: flex;
  align-items: center;
}

.alias-add-btn:hover {
  color: #164863;
  background: #e3f0fa;
}

.alias-add-btn .icon {
  width: 16px;
  height: 16px;
}

.dialog-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(12, 23, 45, 0.13);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.dialog-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 18px #aaa2;
  min-width: 320px;
  padding: 24px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dialog-title {
  font-size: 1.15rem;
  font-weight: 600;
  font-family: 'Archivo', Arial, sans-serif;
  margin: 0;
  color: #164863;
}

.dialog-body {
  font-size: 0.96rem;
  font-family: 'Archivo', Arial, sans-serif;
  color: #555;
}

.dialog-btn {
  padding: 7px 18px;
  border: none;
  border-radius: 5px;
  font-size: 0.98rem;
  font-family: 'Archivo', Arial, sans-serif;
  cursor: pointer;
  background: #164863;
  color: #fff;
  transition: background 0.18s;
}

.dialog-btn.outlined {
  background: #fff;
  color: #1976d2;
  border: 1.5px solid #1976d2;
}

.dialog-btn.error {
  background: #d32f2f;
  color: #fff;
}

.dialog-btn.outlined:hover {
  background: #e3f0fa;
}

.dialog-btn.error:hover {
  background: #b71c1c;
}

/* Alias Management Popup Styles */
.alias-dialog-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px #aaa3;
  min-width: 500px;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;
}

.alias-dialog-header {
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.alias-dialog-body {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.alias-dialog-footer {
  padding: 16px 20px 20px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  background: #fff;
}

.section-label {
  font-size: 12px;
  font-weight: 600;
  font-family: 'Archivo', Arial, sans-serif;
  color: #164863;
  margin-bottom: 6px;
}

.aliases-section {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px;
  background: #fafbfc;
}

.aliases-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.no-aliases-message {
  color: #666;
  font-size: 12px;
  font-family: 'Archivo', Arial, sans-serif;
  font-style: italic;
  text-align: center;
  padding: 8px 0;
}

.alias-tag {
  background: #e3f2fd;
  border: 1px solid #1976d2;
  border-radius: 16px;
  padding: 4px 8px 4px 12px;
  font-size: 12px;
  font-family: 'Archivo', Arial, sans-serif;
  color: #1976d2;
  display: flex;
  align-items: center;
  gap: 6px;
}

.remove-alias-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #1976d2;
  display: flex;
  align-items: center;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  justify-content: center;
  transition: background 0.15s;
}

.remove-alias-btn:hover {
  background: #1976d220;
}

.remove-alias-btn .icon {
  width: 12px;
  height: 12px;
}

.add-alias-section {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px;
}

.alias-input-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.alias-input {
  flex: 1;
  font-size: 12px;
  font-family: 'Archivo', Arial, sans-serif;
  padding: 5px 8px;
  border: 1px solid #c2c8cc;
  border-radius: 4px;
  background: #f9fafd;
  outline: none;
  transition: border 0.12s;
}

.alias-input:focus {
  border-color: #1976d2;
}

.add-btn {
  padding: 5px 10px;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-family: 'Archivo', Arial, sans-serif;
  cursor: pointer;
  background: #1976d2;
  color: #fff;
  transition: background 0.18s;
}

.add-btn:hover:not(:disabled) {
  background: #164863;
}

.add-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.alias-error-message {
  margin-top: 6px;
  padding: 6px 8px;
  background: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
  color: #d32f2f;
  font-size: 11px;
  font-family: 'Archivo', Arial, sans-serif;
  display: flex;
  align-items: center;
  gap: 5px;
  line-height: 1.3;
}

.error-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.instruction-preview-section {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px;
}

.character-count {
  font-size: 10px;
  font-family: 'Archivo', Arial, sans-serif;
  color: #666;
  margin-bottom: 5px;
  text-align: right;
}

.instruction-preview {
  width: 100%;
  font-size: 12px;
  font-family: 'Archivo', Arial, sans-serif;
  padding: 6px 8px;
  border: 1px solid #c2c8cc;
  border-radius: 4px;
  background: #f9fafd;
  outline: none;
  resize: vertical;
  min-height: 50px;
  transition: border 0.12s;
}

.instruction-preview:focus {
  border-color: #1976d2;
}

.instruction-preview.over-limit {
  border-color: #d32f2f;
  background: #ffebee;
}

.or-divider {
  display: flex;
  align-items: center;
  margin: 12px 0;
  gap: 10px;
}

.or-line {
  flex: 1;
  height: 1px;
  background: #e0e0e0;
}

.or-text {
  font-size: 11px;
  font-weight: 600;
  font-family: 'Archivo', Arial, sans-serif;
  color: #666;
  padding: 0 6px;
  background: #fff;
  white-space: nowrap;
}

.ai-generation-section {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.ai-generate-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  border-radius: 5px;
  font-size: 12px;
  font-family: 'Archivo', Arial, sans-serif;
  cursor: pointer;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  transition: transform 0.15s, box-shadow 0.15s;
}

.ai-generate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.ai-generate-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff40;
  border-top: 2px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .blueprint-editor-root {
    flex-direction: column;
  }

  .field-card {
    min-width: unset;
    max-width: 100%;
    width: 100%;
  }

  .field-title-group {
    max-width: 100vw;
  }

  .field-value {
    max-width: 100vw;
  }
}
</style>
